import React, { useState } from 'react';
import { clientApi, formatApiError } from '../../context/ClientAuthContext';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Upload, FileText, Bank, CheckCircle, Folder } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ClientUpload() {
  const [invoiceType, setInvoiceType] = useState('purchase');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [uploadingStatement, setUploadingStatement] = useState(false);
  const [uploadingOther, setUploadingOther] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);

  const handleInvoiceUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    
    let successCount = 0;
    let errorCount = 0;
    const newUploads = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: files.length });
      
      const formData = new FormData();
      formData.append('file', file);

      try {
        await clientApi.post(
          `/upload-invoice?invoice_type=${invoiceType}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        newUploads.push({ type: 'invoice', name: file.name, invoiceType });
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    setRecentUploads([...newUploads, ...recentUploads]);
    setUploadProgress({ current: 0, total: 0 });
    
    if (successCount > 0 && errorCount === 0) {
      toast.success(`${successCount} invoice${successCount > 1 ? 's' : ''} uploaded successfully!`);
    } else if (successCount > 0 && errorCount > 0) {
      toast.warning(`${successCount} uploaded, ${errorCount} failed`);
    } else {
      toast.error('Failed to upload invoices');
    }
    
    setUploading(false);
    e.target.value = '';
  };

  const handleStatementUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingStatement(true);
    const newUploads = [];
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await clientApi.post(
          '/upload-bank-statement',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        newUploads.push({ type: 'statement', name: file.name });
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    setRecentUploads([...newUploads, ...recentUploads]);
    
    if (successCount > 0 && errorCount === 0) {
      toast.success(`${successCount} statement${successCount > 1 ? 's' : ''} uploaded successfully!`);
    } else if (successCount > 0 && errorCount > 0) {
      toast.warning(`${successCount} uploaded, ${errorCount} failed`);
    } else {
      toast.error('Failed to upload statements');
    }
    
    setUploadingStatement(false);
    e.target.value = '';
  };

  const handleOtherUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingOther(true);
    const newUploads = [];
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await clientApi.post(
          `/upload-other-document?document_name=${encodeURIComponent(file.name)}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        newUploads.push({ type: 'other', name: file.name });
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    setRecentUploads([...newUploads, ...recentUploads]);

    if (successCount > 0 && errorCount === 0) {
      toast.success(`${successCount} document${successCount > 1 ? 's' : ''} uploaded successfully!`);
    } else if (successCount > 0 && errorCount > 0) {
      toast.warning(`${successCount} uploaded, ${errorCount} failed`);
    } else {
      toast.error('Failed to upload documents');
    }

    setUploadingOther(false);
    e.target.value = '';
  };

  return (
    <div className="space-y-6" data-testid="client-upload-page">
      <div>
        <h1 className="heading-2 mb-1">Upload Files</h1>
        <p className="text-muted-foreground">Upload your invoices and bank statements for your accountant</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Invoice Upload */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="heading-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Upload Invoice
            </CardTitle>
            <CardDescription>
              Upload purchase or sales invoices. Supported formats: PDF, JPG, PNG
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Invoice Type</Label>
              <Select value={invoiceType} onValueChange={setInvoiceType}>
                <SelectTrigger data-testid="invoice-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="purchase">Purchase Invoice (from suppliers)</SelectItem>
                  <SelectItem value="sales">Sales Invoice (to customers)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-2 border-dashed border-border rounded-md p-8 text-center">
              <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                Drag and drop your invoices here, or click to browse
              </p>
              <input
                type="file"
                id="invoice-upload"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleInvoiceUpload}
                className="hidden"
                disabled={uploading}
                multiple
                data-testid="invoice-file-input"
              />
              <Button
                variant="outline"
                onClick={() => document.getElementById('invoice-upload').click()}
                disabled={uploading}
                data-testid="invoice-upload-btn"
              >
                {uploading 
                  ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` 
                  : 'Select Invoices'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">Select multiple files at once</p>
            </div>
          </CardContent>
        </Card>

        {/* Bank Statement Upload */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="heading-4 flex items-center gap-2">
              <Bank className="w-5 h-5" />
              Upload Bank Statement
            </CardTitle>
            <CardDescription>
              Upload your bank statements. Supported formats: PDF, CSV, Excel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-border rounded-md p-8 text-center">
              <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                Drag and drop your bank statements here, or click to browse
              </p>
              <input
                type="file"
                id="statement-upload"
                accept=".pdf,.csv,.xlsx,.xls"
                onChange={handleStatementUpload}
                className="hidden"
                disabled={uploadingStatement}
                multiple
                data-testid="statement-file-input"
              />
              <Button
                variant="outline"
                onClick={() => document.getElementById('statement-upload').click()}
                disabled={uploadingStatement}
                data-testid="statement-upload-btn"
              >
                {uploadingStatement ? 'Uploading...' : 'Select Statements'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">Select multiple files at once</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Other Documents Upload */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="heading-4 flex items-center gap-2">
            <Folder className="w-5 h-5" />
            Other Documents
          </CardTitle>
          <CardDescription>
            Upload any other documents — contracts, receipts, ID copies, tax returns, etc.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed border-border rounded-md p-8 text-center">
            <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              Drag and drop your documents here, or click to browse
            </p>
            <input
              type="file"
              id="other-upload"
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={handleOtherUpload}
              className="hidden"
              disabled={uploadingOther}
              multiple
              data-testid="other-file-input"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById('other-upload').click()}
              disabled={uploadingOther}
              data-testid="other-upload-btn"
            >
              {uploadingOther ? 'Uploading...' : 'Select Documents'}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">PDF, Word, Excel, Images, Text — Select multiple files</p>
          </div>
        </CardContent>
      </Card>

      {/* Recent Uploads */}
      {recentUploads.length > 0 && (
        <Card className="border border-border">
          <CardHeader className="pb-4">
            <CardTitle className="heading-4">Recent Uploads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentUploads.map((upload, index) => (
                <div
                  key={`${upload.type}-${upload.name}-${index}`}
                  className="flex items-center gap-3 p-3 bg-green-50 rounded-md border border-green-200"
                >
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div className="flex-1">
                    <p className="font-medium text-green-800">{upload.name}</p>
                    <p className="text-sm text-green-600">
                      {upload.type === 'invoice'
                        ? `${upload.invoiceType === 'purchase' ? 'Purchase' : 'Sales'} Invoice`
                        : upload.type === 'other'
                        ? 'Other Document'
                        : 'Bank Statement'}
                    </p>
                  </div>
                  <span className="text-sm text-green-600">Uploaded successfully</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tips */}
      <Card className="border border-border bg-muted/50">
        <CardContent className="py-4">
          <h4 className="font-medium mb-2">Tips for better results:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Make sure documents are clear and readable</li>
            <li>• For invoices, include the full page with all details visible</li>
            <li>• Bank statements should include dates and transaction details</li>
            <li>• Your accountant will be notified when you upload new documents</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
